import { useMemo } from 'react';
import { SettingOutlined, CopyOutlined, DeleteOutlined, InfoCircleOutlined, NodeIndexOutlined } from '@ant-design/icons';

/**
 * Items that should appear in EVERY table's right-click menu.
 *
 * Each item declares its own `visible` rule based on context, so callers can
 * always spread the full result and trust that irrelevant items won't render.
 *
 * @param {Object} ctx
 * @param {Object} ctx.account            - the row's account (or null)
 * @param {Object} [ctx.cellContext]      - optional cell info (period, dataIndex, value)
 * @param {Object} ctx.callbacks
 * @param {Function} ctx.callbacks.onOpenAccountManagement
 * @param {Function} [ctx.callbacks.onShowWhereUsed]
 * @param {Function} [ctx.callbacks.onCopyCellValue]
 * @param {Function} [ctx.callbacks.onDeleteAccount]
 */
export function useSharedRowMenuItems({ account, cellContext, isBCView, callbacks = {} }) {
    const {
        onOpenAccountManagement,
        onShowAccountInfo,
        onShowWhereUsed,
        onCopyCellValue,
        onDeleteAccount,
    } = callbacks;

    return useMemo(() => {
        const items = [];

        // ---- Account Management (available everywhere there's an account) ----
        if (onOpenAccountManagement) {
            items.push({
                key: 'account-management',
                label: 'Account Management',
                icon: <SettingOutlined />,
                visible: account != null && !isBCView,
                onClick: () => onOpenAccountManagement(account),
            });
        }

        if (onShowAccountInfo) {
            items.push({
                key: 'account-info',
                label: 'Account info',
                icon: <InfoCircleOutlined />,
                visible: account != null,
                onClick: () => callbacks.onShowAccountInfo(account),
            });
        }

        if (onShowWhereUsed) {
            items.push({
                key: 'where-used',
                label: 'Where used',
                icon: <NodeIndexOutlined />,
                visible: account != null,
                onClick: () => onShowWhereUsed(account),
            });
        }

        // ---- Future: Copy cell value (only on cell-trigger menus) ----
        if (onCopyCellValue) {
            items.push({
                key: 'copy-cell',
                label: 'Copy value',
                icon: <CopyOutlined />,
                visible: cellContext != null,
                onClick: () => onCopyCellValue(account, cellContext),
            });
        }

        // ---- Future: Delete account ----
        if (onDeleteAccount) {
            items.push({
                key: 'delete-account',
                label: 'Delete account',
                icon: <DeleteOutlined />,
                visible: account != null && !account.children?.length,
                onClick: () => onDeleteAccount(account),
            });
        }

        return items;
    }, [
        account,
        cellContext,
        isBCView,
        onOpenAccountManagement,
        onShowAccountInfo,
        onShowWhereUsed,
        onCopyCellValue,
        onDeleteAccount,
    ]);
}